import {
	CurrentUserRoleLike,
	isOrganizationAdminRole,
} from '../UserSettings/hierarchySelection';

export interface OrganizationAdministratorOption {
	id?: number;
	name: string;
	email?: string;
}

export interface SecurityRoleLookup extends CurrentUserRoleLike {
	'@id'?: string;
}

const getResourceNumericId = (resourceId?: string | null): string | null => {
	if (!resourceId) return null;
	const last = String(resourceId).split('/').filter(Boolean).pop();
	return last || null;
};

const findRoleByResource = (
	resourceId: string | undefined,
	securityRoles: SecurityRoleLookup[]
): SecurityRoleLookup | undefined => {
	if (!resourceId) return undefined;
	return securityRoles.find((role) => {
		if (role['@id'] === resourceId) return true;
		return getResourceNumericId(role['@id']) === getResourceNumericId(resourceId);
	});
};

/**
 * Resolves a user's security role to a name/type object.
 * workplace_employees often returns an IRI only; look that up in the roles list.
 */
export const resolveUserSecurityRole = (
	user: {
		securityRole?: SecurityRoleLookup | string | null;
		_raw?: { securityRole?: SecurityRoleLookup | string | null };
		_full?: { securityRole?: SecurityRoleLookup | string | null };
	},
	securityRoles: SecurityRoleLookup[]
): CurrentUserRoleLike | null => {
	const raw =
		user?.securityRole ?? user?._full?.securityRole ?? user?._raw?.securityRole ?? null;
	if (!raw) return null;

	if (typeof raw === 'string') {
		return findRoleByResource(raw, securityRoles) ?? { name: '' };
	}

	const hasIdentity = Boolean(raw.name || raw.securityRoleType);
	if (hasIdentity) return raw;

	return findRoleByResource(raw['@id'], securityRoles) ?? raw;
};

const getUserDisplayName = (user: {
	name?: string;
	firstName?: string;
	lastName?: string;
}): string =>
	(user.name || `${user.firstName || ''} ${user.lastName || ''}`).trim();

const optionsMatch = (
	option: OrganizationAdministratorOption,
	candidate?: { id?: number; name?: string; email?: string } | null
): boolean => {
	if (!candidate) return false;
	if (option.id != null && candidate.id != null && option.id === candidate.id) return true;
	if (option.email && candidate.email && option.email === candidate.email) return true;
	return Boolean(option.name && candidate.name && option.name === candidate.name);
};

/**
 * Users who already have the Organization Admin role, for the org-record picker.
 * The current administrator is always included so the selected value stays valid.
 */
export const getOrganizationAdministratorOptions = (
	users: Array<{
		id?: number;
		name?: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		securityRole?: SecurityRoleLookup | string | null;
		_raw?: { securityRole?: SecurityRoleLookup | string | null };
		_full?: { securityRole?: SecurityRoleLookup | string | null };
	}>,
	securityRoles: SecurityRoleLookup[],
	currentAdministrator?: { id?: number; name?: string; email?: string } | null
): OrganizationAdministratorOption[] => {
	const options: OrganizationAdministratorOption[] = [];

	for (const user of users) {
		if (!isOrganizationAdminRole(resolveUserSecurityRole(user, securityRoles))) {
			continue;
		}

		const name = getUserDisplayName(user);
		if (!name) continue;

		options.push({
			id: user.id,
			name,
			email: user.email,
		});
	}

	const currentName = currentAdministrator?.name?.trim();
	if (currentName && !options.some((option) => optionsMatch(option, currentAdministrator))) {
		options.push({
			id: currentAdministrator?.id,
			name: currentName,
			email: currentAdministrator?.email,
		});
	}

	return options.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

export const findOrganizationAdministratorOption = (
	options: OrganizationAdministratorOption[],
	currentAdministrator?: { id?: number; name?: string; email?: string } | null
): OrganizationAdministratorOption | null => {
	if (!currentAdministrator?.name && !currentAdministrator?.email && currentAdministrator?.id == null) {
		return null;
	}

	return options.find((option) => optionsMatch(option, currentAdministrator)) || null;
};
